import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { User } from '~api/auth';

import { useAuth } from 'react-oidc-context';

import { IgnoredError } from '~src/errors/IgnoredError';

import { AuthApiInstance, SelfcareApiInstance } from './api';
import { useSiteId } from './hooks/useSiteId';

export interface UseShippingMethodListQueryOptions {
  customBookingMethods?: boolean;
  onlyConfigured?: boolean;
}

const getRolesUserKey = (token?: string) => `_ingrid.userRoles.${token}`;

export function useUserQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['users', siteId] as const,
    get: (token?: string) => [...keys.all, token] as const,
  };

  return keys;
}

export function useGetUserQuery() {
  const queryKey = useUserQueryKey();
  const auth = useAuth();

  return useQuery({
    queryFn: async () => {
      const json = window.sessionStorage.getItem(getRolesUserKey(auth.user?.access_token));

      if (json) {
        return JSON.parse(json) as User;
      }

      try {
        const { data } = await AuthApiInstance.usersGetWithToken.authorizationGetUserWithToken({
          token: auth.user?.access_token,
        });

        if (data.user) {
          window.sessionStorage.setItem(getRolesUserKey(auth.user?.access_token), JSON.stringify(data.user));
        }

        return data.user;
      } catch (e) {
        /**
         * Don't report validation errors.
         *
         * Although this query is prevented unless there's a token and it's not expired, it still frequently happens.
         * Therefore, we're catching it so we can ignore it.
         */
        if (isAxiosError(e) && e.response?.status === 400) {
          throw new IgnoredError('failed to fetch user due to validation error');
        }

        throw e;
      }
    },
    queryKey: queryKey.get(auth.user?.access_token),
    enabled: !!auth.user?.access_token && !auth.user.expired,
  });
}

export function useShippingMethodQueryKey() {
  const siteId = useSiteId();

  const keys = {
    all: ['shippingMethods', siteId] as const,
    lists: () => [...keys.all, 'list'] as const,
    list: (filters: UseShippingMethodListQueryOptions) => [...keys.lists(), { filters }] as const,
  };

  return keys;
}

export async function fetchShippingMethodList(siteId: string, filters: UseShippingMethodListQueryOptions = {}) {
  const { data } = await SelfcareApiInstance.shippingMethodsList.selfCareListShippingMethods({
    site_id: siteId,
    with_custom_booking_methods: filters.customBookingMethods,
  });

  if (!data.methods) {
    return [];
  }

  return data.methods
    .filter(s =>
      (filters.onlyConfigured ?? true) ? s.booking_config_status === 'METHOD_BOOKING_STATUS_CONFIGURED' : true
    )
    .sort(({ name: a = '' }, { name: b = '' }) => (a < b ? -1 : a > b ? 1 : 0));
}

export function useShippingMethodListQuery(filters: UseShippingMethodListQueryOptions = {}) {
  const queryKey = useShippingMethodQueryKey();
  const siteId = useSiteId();

  return useQuery({
    queryFn: async () => fetchShippingMethodList(siteId!, filters),
    queryKey: queryKey.list(filters),
  });
}

export function useShippingMethodListSuspenseQuery(filters: UseShippingMethodListQueryOptions = {}) {
  const queryKey = useShippingMethodQueryKey();
  const siteId = useSiteId();

  return useSuspenseQuery({
    queryFn: async () => fetchShippingMethodList(siteId!, filters),
    queryKey: queryKey.list(filters),
  });
}
